import {Chess} from 'chess.js';
import {StockfishEngine} from './engines/stockfish-engine.js';
(window.FelixChess = window.FelixChess || {}).Chess = Chess;

const ANALYSIS_UPDATE_EVENT = 'fc:analysisupdate';
const ANALYSIS_CLEAR_EVENT = 'fc:analysisclear';
const ANALYSIS_ERROR_EVENT = 'fc:analysiserror';

function isChess960Fen(fen) {
    if (!fen) return false;
    const castling = String(fen).trim().split(/\s+/)[2] || '-';
    return [...castling].some(c => !'KQkq-'.includes(c));
}

const THREAT_MARKERS = [
    {
        from: { class: 'marker-threat-from-1', slice: 'markerSquare' },
        to: { class: 'marker-threat-to-1', slice: 'markerSquare' },
        arrow: { class: 'arrow-threat-1' }
    },
    {
        from: { class: 'marker-threat-from-2', slice: 'markerSquare' },
        to: { class: 'marker-threat-to-2', slice: 'markerSquare' },
        arrow: { class: 'arrow-threat-2' }
    },
    {
        from: { class: 'marker-threat-from-3', slice: 'markerSquare' },
        to: { class: 'marker-threat-to-3', slice: 'markerSquare' },
        arrow: { class: 'arrow-threat-3' }
    }
];

function isValidFenPlacement(placement = '') {
    const ranks = String(placement || '').split('/');
    if (ranks.length !== 8) {
        return false;
    }

    return ranks.every((rank) => {
        let files = 0;
        let previousWasDigit = false;

        for (const char of rank) {
            if (/^[1-8]$/.test(char)) {
                if (previousWasDigit) {
                    return false;
                }
                files += Number(char);
                previousWasDigit = true;
            } else if (/^[pnbrqkPNBRQK]$/.test(char)) {
                files += 1;
                previousWasDigit = false;
            } else {
                return false;
            }
        }

        return files === 8;
    });
}

export function createNullMoveFen(fen) {
    const parts = String(fen || '').trim().split(/\s+/);
    if (parts.length !== 6) {
        return null;
    }

    const [placement, activeColor, castlingRights, , halfmoveClock, fullmoveNumber] = parts;
    if (!isValidFenPlacement(placement) || !/^[wb]$/.test(activeColor)) {
        return null;
    }
    if (!/^(?:-|[KQkq]+)$/.test(castlingRights) || !/^\d+$/.test(halfmoveClock) || !/^\d+$/.test(fullmoveNumber)) {
        return null;
    }

    const nextActiveColor = activeColor === 'w' ? 'b' : 'w';
    return `${placement} ${nextActiveColor} ${castlingRights} - ${halfmoveClock} ${fullmoveNumber}`;
}

export class FelixChessAnalyzer {
    constructor(options = {}) {
        this.rootEl = options.rootEl || null;
        this.analyzerEl = options.analyzerEl || null;
        this.replay = options.replay || null;
        this.context = options.context || 'default';

        this.engineName = options.engineName || 'Stockfish 18 Lite';
        this.engineUrl = options.engineUrl || '';
        this.depth = Number.isFinite(options.depth) ? options.depth : 21;
        this.multiPv = Number.isFinite(options.multiPv) ? options.multiPv : 3;
        this.waitMs = Number.isFinite(options.waitMs) ? options.waitMs : 300;
        this.updateMs = Number.isFinite(options.updateMs) ? options.updateMs : 250;
        this.enableNullMoveAnalysis = options.enableNullMoveAnalysis === true;
        this.enablePgnExport = options.enablePgnExport === true;
        this.onExportPgn = typeof options.onExportPgn === 'function' ? options.onExportPgn : null;
        this.expandedPv = new Set(); // track which lines are expanded

        this.engine = null;
        this.threatEngine = null;
        this.pendingTimer = null;
        this.lastFen = '';
        this.isDestroyed = false;

        this.renderTimer = null;
        this.pendingEnginePayload = null;
        this.threatRenderTimer = null;
        this.pendingThreatPayload = null;
        this.threatNoPvTimer = null;
        this.threatActive = false;
        this.threatFen = '';
        this.threatOriginalFen = '';
        this.threatHeading = '';
        this.currentDepth = 0;
        this.visibilityPaused = false;
        this.boundVisibilityHandler = this.handleVisibilityChange.bind(this);
        document.addEventListener('visibilitychange', this.boundVisibilityHandler);

        this.unsubscribeGameLoaded = null;
        this.unsubscribePositionChange = null;
        this.unsubscribeCleared = null;

        this.handleEngineUpdate = this.handleEngineUpdate.bind(this);
        this.handleEngineReady = this.handleEngineReady.bind(this);
        this.handleEngineError = this.handleEngineError.bind(this);
        this.handleThreatEngineUpdate = this.handleThreatEngineUpdate.bind(this);
        this.handleThreatEngineReady = this.handleThreatEngineReady.bind(this);
        this.handleThreatEngineError = this.handleThreatEngineError.bind(this);

        this.renderShell();
        this.bindReplay();
        this.initEngine();
    }

    handleVisibilityChange() {
        if (this.isDestroyed) {
            return;
        }

        if (document.hidden) {
            // pause analysis
            if (this.engine && this.lastFen) {
                this.engine.stop();
                this.visibilityPaused = true;
                this.setStatus('Paused (tab hidden)');
            }
        } else {
            // resume analysis
            if (this.visibilityPaused && this.lastFen && this.analyzerEl && !this.analyzerEl.hidden) {
                this.visibilityPaused = false;
                this.setStatus('Resuming analysis…');
                this.scheduleAnalysis(this.lastFen);
            }
        }
    }

    renderShell() {
        if (!this.analyzerEl) return;
        const nullMoveButton = this.enableNullMoveAnalysis
            ? `<button type="button"
                       class="fc-boardViewer__threatButton"
                       data-role="nullMoveAnalysisButton"
                       data-fc-null-move-analysis
                       aria-label="Opponent Threats"
                       title="Null Move Analysis: temporarily pass your move and let Stockfish show your opponent’s strongest threats if you do nothing.">
                   Opponent Threats
               </button>`
            : '';
        const exportButton = this.enablePgnExport
            ? `<button type="button"
                       class="fc-boardViewer__exportButton"
                       data-role="exportPgnButton"
                       data-fc-pgn-export
                       aria-label="Export PGN"
                       title="Export selected game as PGN with FelixChess variations">
                   Export PGN
               </button>`
            : '';
        const actionButtons = nullMoveButton || exportButton
            ? `<div class="fc-boardViewer__analyzerActions">${nullMoveButton}${exportButton}</div>`
            : '';

        this.analyzerEl.innerHTML = `
            <div class="fc-boardViewer__analyzerInner" data-role="analyzerInner">
                <div class="fc-boardViewer__analyzerHeader">
                    <div class="fc-boardViewer__analyzerTitle">Analysis</div>
                    <div class="fc-boardViewer__analyzerMeta" data-role="analyzerMeta">
                        ${this.getDepthMetaText()} | ${this.escapeHtml(this.engineName)}
                    </div>
                    ${actionButtons}
                </div>
                <div class="fc-boardViewer__analyzerStatus" data-role="analyzerStatus">
                    Waiting for game…
                </div>
                <div class="fc-boardViewer__analysisLines" data-role="analysisLines"></div>
                <div class="fc-boardViewer__threatPlaceholder" data-role="nullMoveAnalysisPlaceholder" hidden>
                    Opponent Threats will analyze what your opponent threatens if you do nothing.
                </div>
            </div>
        `;

        this.analyzerEl.addEventListener('click', (event) => {
            const threatBtn = event.target.closest('[data-role="nullMoveAnalysisButton"]');
            if (threatBtn) {
                this.handleThreatButtonClick();
                return;
            }

            const exportBtn = event.target.closest('[data-role="exportPgnButton"]');
            if (exportBtn) {
                this.handleExportPgnClick();
                return;
            }

            const btn = event.target.closest('.fc-boardViewer__analysisToggle');
            if (!btn) return;

            const row = btn.closest('.fc-boardViewer__analysisRow');
            if (!row) return;

            const multipv = Number(row.dataset.multipv || '0');
            if (!multipv) return;

            if (this.expandedPv.has(multipv)) {
                this.expandedPv.delete(multipv);
            } else {
                this.expandedPv.add(multipv);
            }

            row.classList.toggle('is-expanded', this.expandedPv.has(multipv));
            btn.setAttribute('aria-expanded', this.expandedPv.has(multipv) ? 'true' : 'false');
            btn.setAttribute('title', this.expandedPv.has(multipv) ? 'Collapse line' : 'Expand line');
            btn.innerHTML = this.expandedPv.has(multipv) ? '&#9650;' : '&#9660;';
        });

        this.analyzerEl.hidden = true;
    }

    handleExportPgnClick() {
        if (!this.enablePgnExport || !this.onExportPgn) {
            return;
        }
        this.onExportPgn();
    }

    getDepthMetaText(maxDepth = this.depth, currentDepth = this.currentDepth) {
        if (Number.isFinite(maxDepth) && maxDepth > 0) {
            return `depth=${currentDepth}/${maxDepth}`;
        }
        return `depth=${currentDepth}`;
    }

    bindReplay() {
        if (!this.replay) return;

        this.unsubscribeGameLoaded = this.replay.on('gameloaded', (state) => {
            this.show();
            this.renderLoading(state);
            this.scheduleAnalysis(state?.currentFen || '');
        });

        this.unsubscribePositionChange = this.replay.on('positionchange', (state) => {
            if (!state?.hasGame) {
                this.clear();
                return;
            }

            this.show();
            this.renderLoading(state);
            this.scheduleAnalysis(state?.currentFen || '');
        });

        this.unsubscribeCleared = this.replay.on('cleared', () => {
            this.clear();
        });
    }

    initEngine() {
        if (!this.engineUrl) {
            console.warn('FelixChessAnalyzer: missing engineUrl');
            return;
        }

        this.engine = new StockfishEngine({
            engineUrl: this.engineUrl,
            depth: this.depth,
            multiPv: this.multiPv,
            onUpdate: this.handleEngineUpdate,
            onReady: this.handleEngineReady,
            onError: this.handleEngineError
        });
    }

    handleEngineReady() {
        this.setStatus('Engine ready');
    }

    handleEngineError(...args) {
        console.error('FelixChessAnalyzer engine error:', ...args);
        this.setStatus('Engine error');
        this.dispatchAnalysisError({
            fen: this.lastFen || '',
            error: args
        });
    }

    initThreatEngine() {
        if (this.threatEngine) {
            return this.threatEngine;
        }

        if (!this.engineUrl) {
            console.warn('FelixChessAnalyzer: missing threat engineUrl');
            return null;
        }

        this.threatEngine = new StockfishEngine({
            engineUrl: this.engineUrl,
            depth: this.depth,
            multiPv: 3,
            onUpdate: this.handleThreatEngineUpdate,
            onReady: this.handleThreatEngineReady,
            onError: this.handleThreatEngineError
        });
        return this.threatEngine;
    }

    handleThreatEngineReady() {
        // Kept separate from the normal engine so threat readiness never changes
        // the main Stockfish analyzer status line.
    }

    handleThreatEngineError(...args) {
        console.error('FelixChessAnalyzer threat engine error:', ...args);
        this.renderThreatMessage('Opponent Threats', 'Opponent Threats are temporarily unavailable.');
        this.clearThreatAnalysis({ hide: false, stop: true });
    }

    async handleThreatButtonClick() {
        if (this.threatActive) {
            this.clearThreatAnalysis();
            return;
        }

        const originalFen = this.lastFen || '';
        const nullMoveFen = createNullMoveFen(originalFen);
        console.log('Null Move Analysis / Opponent Threats clicked', {
            originalFen,
            nullMoveFen
        });

        if (!nullMoveFen) {
            this.renderThreatMessage('Opponent Threats', 'Cannot analyze threats for this position.');
            return;
        }

        const engine = this.initThreatEngine();
        if (!engine) {
            this.renderThreatMessage('Opponent Threats', 'Opponent Threats are temporarily unavailable.');
            return;
        }

        const activeColor = this.extractTurnFromFen(originalFen) === 'b' ? 'Black' : 'White';
        this.threatActive = true;
        this.threatOriginalFen = originalFen;
        this.threatFen = nullMoveFen;
        this.threatHeading = `Threats if ${activeColor} does nothing`;
        this.pendingThreatPayload = null;
        this.renderThreatLoading(this.threatHeading);

        if (this.threatNoPvTimer) {
            window.clearTimeout(this.threatNoPvTimer);
        }
        this.threatNoPvTimer = window.setTimeout(() => {
            this.threatNoPvTimer = null;
            if (this.threatActive && this.threatFen === nullMoveFen) {
                this.renderThreatMessage(this.threatHeading, 'No clear engine threat found.');
            }
        }, 3000);

        try {
            await engine.analyze(nullMoveFen, {
                depth: this.depth,
                multiPv: 3
            });
        } catch (e) {
            console.error('FelixChessAnalyzer: threat analysis failed', e);
            if (this.threatActive && this.threatFen === nullMoveFen) {
                this.renderThreatMessage(this.threatHeading, 'Opponent Threats are temporarily unavailable.');
                this.clearThreatAnalysis({ hide: false, stop: true });
            }
        }
    }

    handleThreatEngineUpdate(payload = {}) {
        if (this.isDestroyed || !this.analyzerEl || !this.threatActive) {
            return;
        }

        const fen = payload.fen || '';
        if (!fen || !this.threatFen || fen !== this.threatFen) {
            return;
        }

        this.pendingThreatPayload = payload;
        if (this.threatRenderTimer) {
            return;
        }

        this.threatRenderTimer = window.setTimeout(() => {
            this.threatRenderTimer = null;

            const latest = this.pendingThreatPayload;
            this.pendingThreatPayload = null;
            if (!latest || this.isDestroyed || !this.analyzerEl || latest.fen !== this.threatFen) {
                return;
            }

            this.applyThreatEngineUpdate(latest);
        }, this.updateMs);
    }

    applyThreatEngineUpdate(payload = {}) {
        const lines = Array.isArray(payload.lines) ? payload.lines : [];
        if (!lines.length) {
            return;
        }

        if (this.threatNoPvTimer) {
            window.clearTimeout(this.threatNoPvTimer);
            this.threatNoPvTimer = null;
        }

        this.renderThreatLines(lines, payload.fen || this.threatFen, payload.depth, payload.maxDepth);
    }

    scheduleAnalysis(fen) {
        if (!fen || !this.engine || this.isDestroyed) {
            return;
        }

        // Register the target FEN immediately so the guard in handleEngineUpdate
        // can block any in-flight results for the previous position.
        this.lastFen = fen;
        this.currentDepth = 0;
        this.pendingEnginePayload = null;
        this.expandedPv.clear();
        const metaEl = this.analyzerEl?.querySelector('[data-role="analyzerMeta"]');
        if (metaEl) {
            metaEl.textContent = `${this.getDepthMetaText()} | ${this.engineName}`;
        }

        if (this.pendingTimer) {
            window.clearTimeout(this.pendingTimer);
            this.pendingTimer = null;
        }

        if (this.renderTimer) {
            window.clearTimeout(this.renderTimer);
            this.renderTimer = null;
        }

        this.engine.stop();

        this.pendingTimer = window.setTimeout(async () => {
            this.pendingTimer = null;

            if (this.isDestroyed || !this.engine || !this.lastFen || document.hidden) {
                if (document.hidden) {
                    this.visibilityPaused = true;
                    this.setStatus('Paused (tab hidden)');
                }
                return;
            }

            try {
                this.setStatus('Analyzing…');
                await this.engine.analyze(this.lastFen, {
                    depth: this.depth,
                    multiPv: this.multiPv
                });
            } catch (e) {
                console.error('FelixChessAnalyzer: analyze failed', e);
                this.setStatus('Analysis failed');
                this.dispatchAnalysisError({
                    fen: this.lastFen || '',
                    error: e
                });
            }
        }, this.waitMs);
    }

    handleEngineUpdate(payload = {}) {
        if (this.isDestroyed || !this.analyzerEl) {
            return;
        }

        const fen = payload.fen || '';
        if (!fen || !this.lastFen || fen !== this.lastFen) {
            return;
        }

        this.pendingEnginePayload = payload;

        if (this.renderTimer) {
            return;
        }

        this.renderTimer = window.setTimeout(() => {
            this.renderTimer = null;

            const latest = this.pendingEnginePayload;
            this.pendingEnginePayload = null;

            if (!latest || this.isDestroyed || !this.analyzerEl) {
                return;
            }

            this.applyEngineUpdate(latest);
        }, this.updateMs);
    }

    applyEngineUpdate(payload = {}) {
        const lines = Array.isArray(payload.lines) ? payload.lines : [];
        const currentDepth = Number.isFinite(payload.depth) ? payload.depth : this.currentDepth;
        const maxDepth = Number.isFinite(payload.maxDepth) ? payload.maxDepth : this.depth;
        const fen = payload.fen || '';

        if (!fen || !this.lastFen || fen !== this.lastFen) {
            return;
        }

        this.currentDepth = Number.isFinite(currentDepth) ? currentDepth : 0;

        const metaEl = this.analyzerEl.querySelector('[data-role="analyzerMeta"]');
        if (metaEl) {
            metaEl.textContent = `${this.getDepthMetaText(maxDepth, this.currentDepth)} | ${this.engineName}`;
        }

        this.renderLines(lines, fen);
        if (lines.length) {
            this.setStatus('');
        }

        this.dispatchAnalysisUpdate({
            fen,
            depth: this.currentDepth,
            maxDepth,
            multiPv: this.multiPv,
            bestLine: lines[0] || null,
            lines
        });
    }

    renderLoading(state) {
        if (!this.analyzerEl) return;

        this.clearThreatAnalysis();
        this.currentDepth = 0;
        const metaEl = this.analyzerEl.querySelector('[data-role="analyzerMeta"]');
        if (metaEl) {
            metaEl.textContent = `${this.getDepthMetaText()} | ${this.engineName}`;
        }

        const linesEl = this.analyzerEl.querySelector('[data-role="analysisLines"]');
        if (linesEl) {
            linesEl.innerHTML = '';
        }

        const currentIndex = Number.isFinite(state?.currentIndex) ? state.currentIndex : 0;
        const suffix = currentIndex > 0 ? ` (ply ${currentIndex})` : '';
        this.setStatus(`Analyzing position${suffix}…`);
    }

    formatPvAsSan(pv = '', fen = '') {
        if (!pv || !fen) {
            return pv || '';
        }

        try {
            const chess = new Chess(fen, { chess960: isChess960Fen(fen) });
            const uciMoves = pv.trim().split(/\s+/).filter(Boolean);
            const parts = [];

            for (const uci of uciMoves) {
                const moveNumber = chess.moveNumber();
                const side = chess.turn();

                const move = this.parseUciMove(uci);
                if (!move) {
                    break;
                }

                const result = chess.move(move);
                if (!result) {
                    break;
                }

                if (side === 'w') {
                    parts.push(`${moveNumber}.${result.san}`);
                } else {
                    if (parts.length === 0) {
                        parts.push(`${moveNumber}...${result.san}`);
                    } else {
                        parts.push(result.san);
                    }
                }
            }

            return parts.join(' ');
        } catch (e) {
            console.warn('FelixChessAnalyzer: failed to format PV as SAN', e);
            return pv;
        }
    }

    parseUciMove(uci) {
        if (!uci || uci.length < 4) {
            return null;
        }

        const move = {
            from: uci.slice(0, 2),
            to: uci.slice(2, 4)
        };

        if (uci.length > 4) {
            move.promotion = uci.slice(4, 5).toLowerCase();
        }

        return move;
    }

    /**
     * Extract the active colour from a FEN string.
     * Returns 'w' or 'b'; falls back to 'w' for malformed input.
     */
    extractTurnFromFen(fen = '') {
        const parts = String(fen || '').trim().split(/\s+/);
        return parts.length >= 2 ? parts[1] : 'w';
    }

    /**
     * Normalise a raw Stockfish score to White's absolute perspective.
     *
     * The UCI protocol mandates that the engine always reports scores from the
     * perspective of the side currently to move.  So if it is Black's turn and
     * the engine says "+231 cp", that means *Black* has a 2.31-pawn advantage.
     * From White's point of view that is −2.31 pawns.
     *
     * We negate the raw value whenever it is Black's turn so that every score
     * displayed to the user follows the universal convention:
     *   positive  →  White is better
     *   negative  →  Black is better
     */
    normalizeScore(rawScore, fen) {
        return this.extractTurnFromFen(fen) === 'b' ? -rawScore : rawScore;
    }

    /**
     * Return a human-readable score string from White's perspective.
     *
     * Uses the structured scoreType / score fields from the engine payload
     * rather than the pre-formatted scoreDisplay string, because we need the
     * raw number to apply the side-to-move inversion.
     */
    buildScoreDisplay(line = {}, fen = '') {
        const scoreType = line.scoreType || null;

        if (scoreType === 'mate' && Number.isFinite(line.score)) {
            const mate = this.normalizeScore(line.score, fen);
            if (mate === 0) return 'M0';
            return mate > 0 ? `#${mate}` : `-#${Math.abs(mate)}`;
        }

        if (scoreType === 'cp' && Number.isFinite(line.score)) {
            const cp = this.normalizeScore(line.score, fen);
            const pawns = cp / 100;
            return `${pawns < 0 ? '-' : ''}${Math.abs(pawns).toFixed(2)}`;
        }

        // Fallback: if there is no structured data, return the engine string
        // unchanged (cannot invert without the raw number).
        return line.scoreDisplay || '—';
    }

    renderLines(lines, fen) {
        if (!this.analyzerEl) return;

        const linesEl = this.analyzerEl.querySelector('[data-role="analysisLines"]');
        if (!linesEl) return;

        if (!lines.length) {
            linesEl.innerHTML = '';
            return;
        }

        linesEl.innerHTML = lines.map((line) => this.renderLine(line, fen)).join('');
    }

    renderThreatLoading(heading) {
        this.renderThreatShell(heading, 'Analyzing opponent threats…', '');
    }

    renderThreatMessage(heading, message) {
        this.renderThreatShell(heading, message, '');
    }

    renderThreatLines(lines, fen, depth, maxDepth) {
        const depthText = Number.isFinite(depth)
            ? `depth=${depth}${Number.isFinite(maxDepth) && maxDepth > 0 ? `/${maxDepth}` : ''}`
            : 'engine eval';
        const threatLines = lines.slice(0, 3);
        this.renderThreatVisuals(threatLines);
        const rows = threatLines
            .map((line) => this.renderThreatLine(line, fen))
            .join('');
        this.renderThreatShell(this.threatHeading || 'Opponent Threats', depthText, rows);
    }

    renderThreatShell(heading, status, bodyHtml) {
        if (!this.analyzerEl) return;

        const threatEl = this.analyzerEl.querySelector('[data-role="nullMoveAnalysisPlaceholder"]');
        if (!threatEl) return;

        threatEl.innerHTML = `
            <div class="fc-boardViewer__threatHeading">${this.escapeHtml(heading || 'Opponent Threats')}</div>
            ${status ? `<div class="fc-boardViewer__threatStatus">${this.escapeHtml(status)}</div>` : ''}
            ${bodyHtml ? `<div class="fc-boardViewer__threatLines">${bodyHtml}</div>` : ''}
        `;
        threatEl.hidden = false;
    }

    renderThreatVisuals(lines = []) {
        const board = this.rootEl && this.rootEl.__fcBoardInstance;
        if (!board || typeof board.addMarker !== 'function') {
            return;
        }

        this.clearThreatVisuals();
        lines.slice(0, 3).forEach((line, index) => {
            const move = this.parseUciMove(String(line?.pv || '').trim().split(/\s+/)[0] || '');
            const markers = THREAT_MARKERS[index];
            if (!move || !markers) {
                return;
            }
            board.addMarker(markers.from, move.from);
            board.addMarker(markers.to, move.to);
            if (typeof board.addArrow === 'function') {
                board.addArrow(markers.arrow, move.from, move.to);
            }
        });
    }

    clearThreatVisuals() {
        const board = this.rootEl && this.rootEl.__fcBoardInstance;
        if (!board) {
            return;
        }

        THREAT_MARKERS.forEach((markers) => {
            if (typeof board.removeMarkers === 'function') {
                board.removeMarkers(markers.from);
                board.removeMarkers(markers.to);
            }
            if (typeof board.removeArrows === 'function') {
                board.removeArrows(markers.arrow);
            }
        });
    }

    renderThreatLine(line = {}, fen = '') {
        const scoreDisplay = this.escapeHtml(this.buildScoreDisplay(line, fen));
        const pvText = this.escapeHtml(this.formatPvAsSan(line.pv || '', fen));

        return `
<div class="fc-boardViewer__analysisRow fc-boardViewer__analysisRow--threat">
    <div class="fc-boardViewer__analysisScore">${scoreDisplay}</div>
    <div class="fc-boardViewer__analysisPv">${pvText}</div>
</div>`;
    }

    renderLine(line = {}, fen = '') {
        const multipv = Number.isFinite(line.multipv) ? line.multipv : 0;
        const isExpanded = this.expandedPv.has(multipv);

        // Use buildScoreDisplay so the score is always from White's perspective,
        // regardless of which side is to move in the current position.
        const scoreDisplay = this.escapeHtml(this.buildScoreDisplay(line, fen));
        const pvText = this.escapeHtml(this.formatPvAsSan(line.pv || '', fen));
        const arrow = isExpanded ? '&#9650;' : '&#9660;'; // ▲ / ▼
        const expandedAttr = isExpanded ? 'true' : 'false';
        const expandedClass = isExpanded ? ' is-expanded' : '';
        const title = isExpanded ? 'Collapse line' : 'Expand line';

        return `
<div class="fc-boardViewer__analysisRow${expandedClass}" data-multipv="${multipv}">
    <div class="fc-boardViewer__analysisScore">${scoreDisplay}</div>
    <div class="fc-boardViewer__analysisPv">${pvText}</div>
    <button
        type="button"
        class="fc-boardViewer__analysisToggle"
        data-multipv="${multipv}"
        aria-expanded="${expandedAttr}"
        title="${title}"
    >${arrow}</button>
</div>`;
    }

    setStatus(text) {
        if (!this.analyzerEl) return;

        const statusEl = this.analyzerEl.querySelector('[data-role="analyzerStatus"]');
        if (!statusEl) return;

        const value = text || '';
        statusEl.textContent = value;
        statusEl.hidden = !value;
    }

    show() {
        if (this.analyzerEl) {
            this.analyzerEl.hidden = false;
        }
    }

    clear() {
        if (this.pendingTimer) {
            window.clearTimeout(this.pendingTimer);
            this.pendingTimer = null;
        }

        if (this.renderTimer) {
            window.clearTimeout(this.renderTimer);
            this.renderTimer = null;
        }

        this.pendingEnginePayload = null;
        this.currentDepth = 0;
        this.visibilityPaused = false;
        this.lastFen = '';
        this.clearThreatAnalysis();

        if (this.engine) {
            this.engine.stop();
        }

        if (!this.analyzerEl) {
            return;
        }

        const linesEl = this.analyzerEl.querySelector('[data-role="analysisLines"]');
        if (linesEl) {
            linesEl.innerHTML = '';
        }

        const metaEl = this.analyzerEl.querySelector('[data-role="analyzerMeta"]');
        if (metaEl) {
            metaEl.textContent = `${this.getDepthMetaText()} | ${this.engineName}`;
        }

        this.setStatus('Waiting for game…');
        this.analyzerEl.hidden = true;
        this.dispatchAnalysisClear();
        this.expandedPv.clear();
    }

    clearThreatAnalysis(options = {}) {
        const hide = options.hide !== false;
        const stop = options.stop !== false;

        this.threatActive = false;
        this.threatFen = '';
        this.threatOriginalFen = '';
        this.threatHeading = '';
        this.pendingThreatPayload = null;
        this.clearThreatVisuals();

        if (this.threatRenderTimer) {
            window.clearTimeout(this.threatRenderTimer);
            this.threatRenderTimer = null;
        }

        if (this.threatNoPvTimer) {
            window.clearTimeout(this.threatNoPvTimer);
            this.threatNoPvTimer = null;
        }

        if (stop && this.threatEngine) {
            this.threatEngine.stop();
        }

        if (hide && this.analyzerEl) {
            const threatEl = this.analyzerEl.querySelector('[data-role="nullMoveAnalysisPlaceholder"]');
            if (threatEl) {
                threatEl.hidden = true;
                threatEl.innerHTML = '';
            }
        }
    }

    escapeHtml(value) {
        return String(value ?? '')
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;')
            .replaceAll("'", '&#039;');
    }

    dispatchAnalysisUpdate(detail = {}) {
        if (!this.rootEl) {
            return;
        }

        this.rootEl.dispatchEvent(new CustomEvent(ANALYSIS_UPDATE_EVENT, {
            detail,
            bubbles: true
        }));
    }

    dispatchAnalysisClear() {
        if (!this.rootEl) {
            return;
        }

        this.rootEl.dispatchEvent(new CustomEvent(ANALYSIS_CLEAR_EVENT));
    }

    dispatchAnalysisError(detail = {}) {
        if (!this.rootEl) {
            return;
        }

        this.rootEl.dispatchEvent(new CustomEvent(ANALYSIS_ERROR_EVENT, {
            detail
        }));
    }

    destroy() {
        this.isDestroyed = true;

        if (this.pendingTimer) {
            window.clearTimeout(this.pendingTimer);
            this.pendingTimer = null;
        }

        if (this.renderTimer) {
            window.clearTimeout(this.renderTimer);
            this.renderTimer = null;
        }
        this.pendingEnginePayload = null;
        this.clearThreatAnalysis();

        if (this.unsubscribeGameLoaded) {
            this.unsubscribeGameLoaded();
            this.unsubscribeGameLoaded = null;
        }

        if (this.unsubscribePositionChange) {
            this.unsubscribePositionChange();
            this.unsubscribePositionChange = null;
        }

        if (this.unsubscribeCleared) {
            this.unsubscribeCleared();
            this.unsubscribeCleared = null;
        }

        if (this.engine) {
            this.engine.destroy();
            this.engine = null;
        }

        if (this.threatEngine) {
            this.threatEngine.destroy();
            this.threatEngine = null;
        }

        document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
    }
}
